<template>
  <section class="p-4 md:p-6 bg-gray-50">
    <header class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">
        effect数据卡片列表 {{ list.length }}
      </h1>
    </header>

    <div v-if="pending" class="text-sm text-gray-500">数据加载中...</div>
    <div v-else-if="error" class="text-sm text-red-600">
      数据加载失败：{{ error.message }}
    </div>

    <div
      v-else
      class="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-5 items-start"
    >
      <article
        v-for="(item, index) in list"
        :key="`${item.code}-${index}`"
        class="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4"
      >
        <button
          type="button"
          class="w-full text-left"
          @click="toggleCard(index)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="space-y-1 min-w-0">
              <h2 class="text-lg font-semibold text-gray-900 break-all">
                {{ item.title || "-" }}
              </h2>
              <p class="text-xs text-gray-500 break-all">
                code: {{ item.code || "-" }}
              </p>
            </div>
            <span class="text-xs text-gray-500 whitespace-nowrap mt-1">
              {{ isCardOpen(index) ? "收起全部" : "展开全部" }}
            </span>
          </div>
        </button>

        <template v-if="isCardOpen(index)">
          <div class="space-y-2">
            <h3 class="font-medium text-gray-800">
              初始 mp4（{{ item.size?.["初始mp4"] || "-" }}）
            </h3>
            <div
              v-if="item['初始mp4']"
              class="mx-auto w-[320px] h-[180px] max-w-full bg-black rounded-lg overflow-hidden"
            >
              <video
                :src="item['初始mp4']"
                controls
                preload="metadata"
                playsinline
                class="w-full h-full object-contain bg-black"
              />
            </div>
            <p v-else class="text-sm text-gray-400">无</p>
          </div>

          <div class="space-y-2">
            <h3 class="font-medium text-gray-800">
              压缩初始 mp4（{{ item.size?.["压缩初始mp4"] || "-" }}）
            </h3>
            <div
              v-if="item['压缩初始mp4']"
              class="mx-auto w-[320px] h-[180px] max-w-full bg-black rounded-lg overflow-hidden"
            >
              <video
                :src="item['压缩初始mp4']"
                controls
                preload="metadata"
                playsinline
                class="w-full h-full object-contain bg-black"
              />
            </div>
            <p v-else class="text-sm text-gray-400">无</p>
          </div>

          <div class="space-y-2">
            <h3 class="font-medium text-gray-800">
              压缩初始 mp4 转 webp（{{
                item.size?.["压缩初始mp4转webp"] || "-"
              }}）
            </h3>
            <div
              v-if="item['压缩初始mp4转webp']"
              class="mx-auto w-[320px] h-[180px] max-w-full bg-black rounded-lg overflow-hidden"
            >
              <img
                :src="item['压缩初始mp4转webp']"
                alt="压缩初始 mp4 转 webp"
                loading="lazy"
                decoding="async"
                class="w-full h-full object-contain bg-black"
              />
            </div>
            <p v-else class="text-sm text-gray-400">无</p>
          </div>

          <div class="space-y-2">
            <h3 class="font-medium text-gray-800">BGM mp3</h3>
            <audio
              v-if="item.bgmmp3"
              :src="item.bgmmp3"
              controls
              preload="metadata"
              class="w-full"
            />
            <p v-else class="text-sm text-gray-400">无</p>
          </div>

          <div class="space-y-2">
            <h3 class="font-medium text-gray-800">
              合并 BGM 的 mp4（{{ item.size?.["合并bgm的mp4"] || "-" }}）
            </h3>
            <div
              v-if="item['合并bgm的mp4']"
              class="mx-auto w-[320px] h-[180px] max-w-full bg-black rounded-lg overflow-hidden"
            >
              <video
                :src="item['合并bgm的mp4']"
                controls
                preload="metadata"
                playsinline
                class="w-full h-full object-contain bg-black"
              />
            </div>
            <p v-else class="text-sm text-gray-400">无</p>
          </div>

          <div class="space-y-2">
            <h3 class="font-medium text-gray-800">
              Pollo 封面 mp4（{{
                item.size?.["pollo原mp4"]?.["封面mp4"] || "-"
              }}）
            </h3>
            <div
              v-if="item['pollo原mp4']?.['封面mp4']"
              class="mx-auto w-[320px] h-[180px] max-w-full bg-black rounded-lg overflow-hidden"
            >
              <video
                :src="item['pollo原mp4']?.['封面mp4'] || ''"
                controls
                preload="metadata"
                playsinline
                class="w-full h-full object-contain bg-black"
              />
            </div>
            <p v-else class="text-sm text-gray-400">无</p>
          </div>

          <div class="space-y-2">
            <h3 class="font-medium text-gray-800">
              Pollo 作品 mp4（共 {{ getWorkVideos(item).length }} 个）
            </h3>
            <div class="space-y-3">
              <div
                v-for="(videoUrl, videoIndex) in getWorkVideos(item)"
                :key="videoUrl"
                class="border border-gray-200 rounded-lg p-2"
              >
                <p class="text-xs text-gray-500 mb-2">
                  作品 {{ videoIndex + 1 }}（{{
                    item.size?.["pollo原mp4"]?.["作品mp4"]?.[videoIndex] || "-"
                  }}）
                </p>
                <div
                  class="mx-auto w-[320px] h-[180px] max-w-full bg-black rounded-lg overflow-hidden"
                >
                  <video
                    :src="videoUrl"
                    controls
                    preload="metadata"
                    playsinline
                    class="w-full h-full object-contain bg-black"
                  />
                </div>
              </div>
            </div>
            <p v-if="!hasWorkVideos(item)" class="text-sm text-gray-400">无</p>
          </div>
        </template>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useAsyncData, useRequestFetch } from "#imports";
import { computed, reactive } from "vue";

interface EffectSize {
  ["初始mp4"]?: string;
  ["压缩初始mp4"]?: string;
  ["压缩初始mp4转webp"]?: string;
  ["合并bgm的mp4"]?: string;
  ["pollo原mp4"]?: {
    ["封面mp4"]?: string;
    ["作品mp4"]?: string[];
  };
}

interface EffectItem {
  code: string;
  title: string;
  ["初始mp4"]?: string;
  ["压缩初始mp4"]?: string;
  ["压缩初始mp4转webp"]?: string;
  bgmmp3?: string;
  ["合并bgm的mp4"]?: string;
  ["pollo原mp4"]?: {
    ["封面mp4"]?: string;
    ["作品mp4"]?: string[];
  };
  size?: EffectSize;
}

const parseEffectList = (raw: unknown): EffectItem[] => {
  if (Array.isArray(raw)) return raw as EffectItem[];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as EffectItem[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const r2CachedFetch = useR2CachedFetch();
const { data, pending, error } = await useAsyncData(
  "test-3-effect-list",
  async () => {
    const res = await r2CachedFetch("/api/r2/ai/effects/summary_data.json");
    return parseEffectList(res);
  },
  {
    default: () => [],
  }
);

const list = computed(() => data.value || []);

const getWorkVideos = (item: EffectItem) =>
  item["pollo原mp4"]?.["作品mp4"] || [];

const hasWorkVideos = (item: EffectItem) => getWorkVideos(item).length > 0;

const openCards = reactive(new Set<number>());

const isCardOpen = (cardIndex: number) => openCards.has(cardIndex);

const toggleCard = (cardIndex: number) => {
  if (openCards.has(cardIndex)) {
    openCards.delete(cardIndex);
    return;
  }
  openCards.add(cardIndex);
};
</script>
